import styled, { css, keyframes, type DefaultTheme } from 'styled-components';

const primaryBoldFor = (theme: DefaultTheme): string =>
  theme.colors?.primaryBold ?? '#8E8DFF';

const runErrorFor = (theme: DefaultTheme): string =>
  theme.colors?.runError ?? '#FF6B6B';

const isLightTheme = (theme: DefaultTheme): boolean => !!theme.isLightTheme;

// Matches design's `@keyframes ns-wire-flow { to { stroke-dashoffset: -18; } }`
// at the canonical 1.2s linear cadence (styles.css L3453).
const flow = keyframes`
  to { stroke-dashoffset: -18; }
`;

export interface GhostPathProps {
  /** Mirrors React Flow's `connectionStatus`: 'invalid' tints the ghost red. */
  $invalid: boolean;
}

// Ghost wire shown while the user is dragging from a port. Width and
// dash pattern are slightly thinner than the resting wire so it reads
// as "in flight" without competing with the canvas chrome.
export const GhostPath = styled.path<GhostPathProps>`
  fill: none;
  stroke-width: 2;
  stroke-dasharray: 5 4;
  stroke-linecap: round;
  stroke-linejoin: round;
  pointer-events: none;
  opacity: 1;
  animation: ${flow} 1.2s linear infinite;
  ${({ $invalid, theme }) => {
    if ($invalid) {
      const red = runErrorFor(theme);
      return css`
        stroke: ${red};
        filter: drop-shadow(
          0 0 5px color-mix(in oklch, ${red} 45%, transparent)
        );
      `;
    }
    const primary = primaryBoldFor(theme);
    // Light skips the white-mix lighten — the design's `color-mix(primary 90%, white)`
    // pushes to near-white pastel on lavender paper.
    const stroke = isLightTheme(theme)
      ? primary
      : `color-mix(in oklch, ${primary} 90%, white)`;
    return css`
      stroke: ${stroke};
      filter: drop-shadow(
        0 0 4px color-mix(in oklch, ${primary} 38%, transparent)
      );
    `;
  }}
`;
